/**
 * Tracker sitewide de engagement de imágenes Cloudinary. Montar una sola vez (App.tsx).
 *
 * Recolecta, sin instrumentar cada galería a mano:
 *   - IMPRESIONES: IntersectionObserver sobre cada <img> Cloudinary (entró al
 *     viewport ≥50% ≥600ms) → exposición, denominador del CTR.
 *   - APERTURAS / TAPS: listeners delegados click/touchend.
 *   - ZOOM: dblclick / rueda+ctrl sobre una imagen Cloudinary → señal de interés.
 *   - Un MutationObserver re-observa imágenes nuevas (SPA, lazy-load, lightbox).
 *   - FLUSH del resumen de sesión en pagehide / visibility hidden.
 */
import { useEffect } from "react";
import {
  trackCloudinaryImageView,
  recordImageImpression,
  recordImageZoom,
  flushImageSession,
  isCloudinaryDeliveryUrl,
} from "@/lib/imageViewTracking";

function imgUrl(el: Element | null): string | null {
  if (el instanceof HTMLImageElement) {
    const url = el.currentSrc || el.src;
    return isCloudinaryDeliveryUrl(url) ? url : null;
  }
  return null;
}

function extractBgUrl(el: Element): string | null {
  const inline = (el as HTMLElement).style?.backgroundImage;
  const computed =
    typeof window !== "undefined" ? window.getComputedStyle(el).backgroundImage : "";
  const raw = inline && inline !== "none" ? inline : computed;
  if (!raw || raw === "none") return null;
  const m = raw.match(/url\(["']?(.*?)["']?\)/);
  const url = m?.[1];
  return isCloudinaryDeliveryUrl(url) ? url! : null;
}

function metaFor(el: Element, alt?: string) {
  return {
    altText: alt || (el as HTMLElement).getAttribute?.("aria-label") || undefined,
    section:
      el.closest<HTMLElement>("[data-gallery-section]")?.dataset.gallerySection ||
      undefined,
  };
}

/** Sube por el DOM buscando la primera imagen Cloudinary asociada al gesto. */
function findCloudinaryImage(
  start: Element | null,
): { url: string; alt?: string; section?: string } | null {
  let el: Element | null = start;
  let depth = 0;
  while (el && depth < 6) {
    const direct = imgUrl(el);
    if (direct) return { url: direct, ...metaFor(el, (el as HTMLImageElement).alt) };
    const bg = extractBgUrl(el);
    if (bg) return { url: bg, ...metaFor(el) };
    const innerImg = el.querySelector?.("img");
    const inner = imgUrl(innerImg ?? null);
    if (inner) return { url: inner, ...metaFor(el, (innerImg as HTMLImageElement).alt) };
    el = el.parentElement;
    depth++;
  }
  return null;
}

export function CloudinaryImageTracker() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const tpl = () => document.documentElement.dataset.template || undefined;

    // ── Aperturas / taps ──────────────────────────────────────────────────────
    const onInteract = (interaction: "open" | "touch") => (ev: Event) => {
      const found = findCloudinaryImage(ev.target as Element | null);
      if (!found) return;
      trackCloudinaryImageView(found.url, {
        altText: found.alt,
        section: found.section,
        interactionType: interaction,
        template: tpl(),
      });
    };
    const clickHandler = onInteract("open");
    const touchHandler = onInteract("touch");
    document.addEventListener("click", clickHandler, { capture: true, passive: true });
    document.addEventListener("touchend", touchHandler, { capture: true, passive: true });

    // ── Zoom (doble click o rueda + ctrl) ─────────────────────────────────────
    const dblHandler = (ev: Event) => {
      const found = findCloudinaryImage(ev.target as Element | null);
      if (found) recordImageZoom(found.url, { altText: found.alt, section: found.section, template: tpl() });
    };
    const wheelHandler = (ev: WheelEvent) => {
      if (!ev.ctrlKey) return; // pinch-zoom en trackpad llega como wheel+ctrl
      const found = findCloudinaryImage(ev.target as Element | null);
      if (found) recordImageZoom(found.url, { altText: found.alt, section: found.section, template: tpl() });
    };
    document.addEventListener("dblclick", dblHandler, { capture: true, passive: true });
    document.addEventListener("wheel", wheelHandler, { capture: true, passive: true });

    // ── Impresiones (IntersectionObserver + MutationObserver) ─────────────────
    const seen = new WeakSet<Element>();
    const timers = new WeakMap<Element, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (timers.has(el)) continue;
            const t = window.setTimeout(() => {
              timers.delete(el);
              const url = imgUrl(el);
              if (url) recordImageImpression(url, metaFor(el, (el as HTMLImageElement).alt));
            }, 600);
            timers.set(el, t);
          } else {
            const t = timers.get(el);
            if (t != null) {
              clearTimeout(t);
              timers.delete(el);
            }
          }
        }
      },
      { threshold: [0, 0.5, 1] },
    );

    const observeImg = (el: Element) => {
      if (seen.has(el)) return;
      if (!(el instanceof HTMLImageElement)) return;
      seen.add(el);
      io.observe(el);
    };
    const scan = (root: ParentNode) => {
      root.querySelectorAll?.("img").forEach((img) => {
        const url = img.currentSrc || img.src;
        if (isCloudinaryDeliveryUrl(url)) observeImg(img);
      });
    };
    scan(document);

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          const el = n as Element;
          if (el.tagName === "IMG") {
            const url = (el as HTMLImageElement).currentSrc || (el as HTMLImageElement).src;
            if (isCloudinaryDeliveryUrl(url)) observeImg(el);
          } else {
            scan(el as ParentNode);
          }
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // ── Flush del resumen de sesión al salir ──────────────────────────────────
    const onHide = () => {
      if (document.visibilityState === "hidden") flushImageSession();
    };
    const onPageHide = () => flushImageSession();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      document.removeEventListener("click", clickHandler, { capture: true } as EventListenerOptions);
      document.removeEventListener("touchend", touchHandler, { capture: true } as EventListenerOptions);
      document.removeEventListener("dblclick", dblHandler, { capture: true } as EventListenerOptions);
      document.removeEventListener("wheel", wheelHandler, { capture: true } as EventListenerOptions);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      io.disconnect();
      mo.disconnect();
      flushImageSession();
    };
  }, []);

  return null;
}
